import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Gauge, Loader2, CloudOff, Package, MessageSquare, Users } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { PLAN_CLS } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

const PLAN_ORDER = ["FREE", "LIGHT", "WEBPRO", "MULTI"];

export default function PlaeneNutzung() {
  const { user } = useAuth();
  const scope = user?.role === "super_admin" ? "all" : "customers";
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations/summary/plans", { params: { scope } })
      .then((r) => setState(r.data))
      .catch(() => setState({ service_available: false, plans: {}, organizations: [], reason: "FUNDIO Tenant Service nicht erreichbar." }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  if (state === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;

  const plans = state.plans || {};
  const orgs = state.organizations || [];
  const planKeys = [...new Set([...PLAN_ORDER.filter((p) => plans[p]), ...Object.keys(plans)])];

  return (
    <div data-testid="plaene-page">
      <PageHeader title="Pläne & Nutzung" subtitle="Plan-Verteilung und Nutzung (Objekte, Anfragen, Mitgliedschaften) je FUNDIO-Kunde – read-only aus dem kanonischen Tenant-System." />

      {!state.service_available ? (
        <div data-testid="plaene-unavailable" className="card-surface flex flex-col items-center rounded-xl px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-danger-bg"><CloudOff className="h-6 w-6 text-status-danger" /></span>
          <p className="mt-4 text-base font-semibold text-fundio-ink">FUNDIO Tenant Service nicht erreichbar</p>
          <p className="mt-1 text-sm text-fundio-muted">{state.reason}</p>
        </div>
      ) : (
        <>
          <div data-testid="plaene-kpis" className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {planKeys.length === 0 && (
              <div className="card-surface col-span-2 rounded-xl p-5 text-sm text-fundio-muted lg:col-span-4">Keine Pläne im gewählten Bereich.</div>
            )}
            {planKeys.map((p) => (
              <div key={p} data-testid={`plaene-card-${p}`} className="card-surface rounded-xl p-5">
                <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[p] || PLAN_CLS.FREE}`}>{p}</span>
                <div className="mt-3 font-display text-3xl font-bold tracking-tight text-fundio-ink">{plans[p].count}</div>
                <div className="mt-0.5 text-xs text-fundio-muted">Kunden</div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fundio-faint">
                  <span className="inline-flex items-center gap-1"><Package className="h-3 w-3" />{plans[p].items}</span>
                  <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{plans[p].inquiries}</span>
                  <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{plans[p].memberships}</span>
                </div>
              </div>
            ))}
          </div>

          <Panel title={`Nutzung je Kunde (${orgs.length})`} testid="plaene-panel">
            {!orgs.length ? (
              <div className="p-6 text-sm text-fundio-muted">Keine Kunden im gewählten Bereich.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint">
                      <th className="px-2 py-2 font-medium">Kunde</th>
                      <th className="px-2 py-2 font-medium">Plan</th>
                      <th className="px-2 py-2 font-medium">Objekte</th>
                      <th className="px-2 py-2 font-medium">Anfragen</th>
                      <th className="px-2 py-2 font-medium">Mitglieder</th>
                      <th className="px-2 py-2 font-medium">Speicher CHF/GB</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-fundio-border">
                    {orgs.map((o) => (
                      <tr key={o.tenant_id} data-testid={`plaene-row-${o.tenant_id}`} className="transition-colors hover:bg-fundio-subtle">
                        <td className="px-2 py-3 font-medium text-fundio-ink">
                          {o.company_name || o.tenant_id}
                          {o.is_demo && <span className="ml-2 inline-flex items-center rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-status-warning">QA</span>}
                        </td>
                        <td className="px-2 py-3">{o.plan ? <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[o.plan] || PLAN_CLS.FREE}`}>{o.plan}</span> : "—"}</td>
                        <td className="px-2 py-3 text-fundio-ink">{o.item_count ?? "—"}</td>
                        <td className="px-2 py-3 text-fundio-ink">{o.inquiry_count ?? "—"}</td>
                        <td className="px-2 py-3 text-fundio-ink">{o.membership_count ?? "—"}</td>
                        <td className="px-2 py-3 text-fundio-muted">{o.storage_cost_per_gb_chf ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
